import { EntityContext, query } from "@archipad-js/core/orm";

import { VisitEntity } from "../models/VisitEntity";
import { VisitMemberEntity } from "../models/VisitMemberEntity";

export enum VisitMemberPresenceTypes {
    Absent = 0,
    Present = 1,
    Excused = 2,
    Late = 3,
    Unknown = -1,
}

export const ValidVisitMemberPresenceTypes: ReadonlyArray<VisitMemberPresenceTypes> = [
    VisitMemberPresenceTypes.Absent,
    VisitMemberPresenceTypes.Present,
    VisitMemberPresenceTypes.Excused,
    VisitMemberPresenceTypes.Late,
];

export function isValidPresenceType(type: number | null): type is VisitMemberPresenceTypes {
    return ValidVisitMemberPresenceTypes.includes(type as VisitMemberPresenceTypes);
}

/**
 * Copy of the algorithm defined in VisitMemberModel.m in Archipad App
 */
export class VisitMemberPresenceCounters {
    public constructor(private readonly entityContext: EntityContext) {}

    /**
     * Get the presence counters for the given visitMember
     * @param visitMember
     */
    public getCountersForVisitMember(visitMember: VisitMemberEntity): {
        lateCount: number;
        presenceCount: number;
        excusedCount: number;
        absenceCount: number;
    } {
        // iterate over previous visits.
        const q = query<VisitEntity>(this.entityContext, "Visit");
        q.orderBy("region.name(natural), date desc, object(natural)");
        const resultSet = q.execute();
        const sortedVisits = resultSet.sortedEntities();

        const counters = new Map<VisitMemberPresenceTypes, number>();
        const countersSealed = new Map<VisitMemberPresenceTypes, boolean>();
        for (const visitMemberPresenceType of ValidVisitMemberPresenceTypes) {
            counters.set(visitMemberPresenceType, 0);
            countersSealed.set(visitMemberPresenceType, false);
        }

        // iterate through all visits after this one
        const currentVisitIndex = sortedVisits.indexOf(visitMember.visit);
        for (const visit of sortedVisits.splice(currentVisitIndex)) {
            // find back visit member for this person, the visit member might not exist in this visit
            for (const member of visit.members) {
                if (member.person !== visitMember.person) {
                    continue;
                }

                const presenceType = member.presenceType;

                if (isValidPresenceType(presenceType)) {
                    if (!countersSealed.get(presenceType)) {
                        counters.set(presenceType, (counters.get(presenceType) ?? 0) + 1);
                    }
                }

                // handle resets
                let sealedChanged = false;
                let resetCount: number;
                let visitMemberPresenceType: VisitMemberPresenceTypes;

                resetCount = member.absenceResetCount ?? 0;
                visitMemberPresenceType = VisitMemberPresenceTypes.Absent;
                if (!countersSealed.get(visitMemberPresenceType) && resetCount) {
                    countersSealed.set(visitMemberPresenceType, true);
                    counters.set(visitMemberPresenceType, (counters.get(visitMemberPresenceType) ?? 0) + resetCount);
                    sealedChanged = true;
                }
                resetCount = member.presenceResetCount ?? 0;
                visitMemberPresenceType = VisitMemberPresenceTypes.Present;
                if (!countersSealed.get(visitMemberPresenceType) && resetCount) {
                    countersSealed.set(visitMemberPresenceType, true);
                    counters.set(visitMemberPresenceType, (counters.get(visitMemberPresenceType) ?? 0) + resetCount);
                    sealedChanged = true;
                }
                resetCount = member.excusedResetCount ?? 0;
                visitMemberPresenceType = VisitMemberPresenceTypes.Excused;
                if (!countersSealed.get(visitMemberPresenceType) && resetCount) {
                    countersSealed.set(visitMemberPresenceType, true);
                    counters.set(visitMemberPresenceType, (counters.get(visitMemberPresenceType) ?? 0) + resetCount);
                    sealedChanged = true;
                }
                resetCount = member.lateResetCount ?? 0;
                visitMemberPresenceType = VisitMemberPresenceTypes.Late;
                if (!countersSealed.get(visitMemberPresenceType) && resetCount) {
                    countersSealed.set(visitMemberPresenceType, true);
                    counters.set(visitMemberPresenceType, (counters.get(visitMemberPresenceType) ?? 0) + resetCount);
                    sealedChanged = true;
                }

                if (sealedChanged) {
                    let sealed = true;
                    for (const visitMemberPresenceTypesKey of ValidVisitMemberPresenceTypes) {
                        sealed = sealed && countersSealed.has(visitMemberPresenceTypesKey);
                    }
                    if (sealed) {
                        return {
                            lateCount: counters.get(VisitMemberPresenceTypes.Late) ?? 0,
                            presenceCount: counters.get(VisitMemberPresenceTypes.Present) ?? 0,
                            excusedCount: counters.get(VisitMemberPresenceTypes.Excused) ?? 0,
                            absenceCount: counters.get(VisitMemberPresenceTypes.Absent) ?? 0,
                        };
                    }
                }
            }
        }

        return {
            lateCount: counters.get(VisitMemberPresenceTypes.Late) ?? 0,
            presenceCount: counters.get(VisitMemberPresenceTypes.Present) ?? 0,
            excusedCount: counters.get(VisitMemberPresenceTypes.Excused) ?? 0,
            absenceCount: counters.get(VisitMemberPresenceTypes.Absent) ?? 0,
        };
    }
}
